import React, { useCallback, useEffect, useState } from 'react';
import {
  Panel, PanelGrid, StatGrid, StatTile, SimpleTable, Tag, Button,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { exportDashboardCsv, SOURCE_LABELS, EXAM_TYPE_LABELS } from './RegistrationsDashboard.jsx';

/**
 * דשבורד רישומים — new design. Same endpoint and CSV export as RegistrationsDashboard:
 * summary tiles (+ per source), registrations by exam city (collapsible per city), by exam and by kolel.
 */
const codeTag = (v) => (v != null ? <Tag>{v}</Tag> : '—');

export default function RegistrationsDashboardNext({ periodId, periodLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCities, setOpenCities] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/reports/registrations-dashboard', { params: periodId ? { periodId } : {} });
      setData(res);
    } catch {
      /* the list below still works */
    } finally {
      setLoading(false);
    }
  }, [periodId]);
  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const byExam = data?.byExam || [];
  const byKolel = data?.byKolel || [];
  const bySource = data?.bySource || [];
  const byExamCity = data?.byExamCity || [];
  const first = loading && !data;
  const toggleCity = (k) => setOpenCities((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <Panel
      title="דשבורד רישומים"
      sub={periodLabel}
      icon="chart"
      collapsible
      defaultOpen
      actions={(
        <>
          <Button size="sm" variant="ghost" icon="download" disabled={!data || loading} onClick={() => exportDashboardCsv({ byKolel, byExam, byExamCity, summary, periodLabel })}>ייצוא (לפי עיר)</Button>
          <Button size="sm" variant="ghost" icon="refresh" onClick={load} aria-label="רענון" />
        </>
      )}
    >
      <div className="nx-stack">
        <StatGrid>
          <StatTile label="נבחנים ייחודיים" value={summary.uniqueExaminees ?? 0} icon="users" tone="primary" loading={first} />
          <StatTile label="סה״כ רישומים" value={summary.totalRegistrations ?? 0} icon="task" tone="info" loading={first} />
          <StatTile label="כוללים עם רישומים" value={summary.totalKolels ?? 0} icon="building" tone="success" loading={first} />
          {bySource.map((s) => <StatTile key={s.source} label={`מקור: ${SOURCE_LABELS[s.source] || s.source}`} value={s.count} icon={s.source === 'ivr' ? 'phone' : s.source === 'import' ? 'upload' : 'link'} loading={first} />)}
        </StatGrid>

        <Panel title="רשומים לפי עיר בחינה" sub={`${byExamCity.length} ערים`} icon="building" flush>
          {byExamCity.length === 0 ? <div className="ck-empty"><span className="ck-muted">{first ? 'טוען…' : 'אין נתונים'}</span></div> : (
            <div className="nx-list">
              {byExamCity.map((city) => {
                const key = city.location || 'ללא עיר';
                const open = openCities.has(key);
                const exams = [...(city.exams || [])].sort((a, b) => (a.code ?? 0) - (b.code ?? 0));
                return (
                  <div key={key} className="nx-stack" style={{ gap: 0 }}>
                    <button type="button" className={`nx-item nx-item--btn ${open ? 'is-on' : ''}`} onClick={() => toggleCity(key)} aria-expanded={open}>
                      <span className="nx-item__main"><b>{key}</b><small>{`${exams.length} מבחנים`}</small></span>
                      <Tag tone="primary">{`סה"כ: ${city.total}`}</Tag>
                    </button>
                    {open ? (
                      <SimpleTable
                        rowKey="code"
                        rows={[...exams, { code: '__total', _total: true, count: city.total }]}
                        rowClassName={(r) => (r._total ? 'is-total' : '')}
                        columns={[
                          { key: 'code', label: 'קוד', render: (r) => (r._total ? <b>{`סה"כ ${key}`}</b> : codeTag(r.code)) },
                          { key: 'name', label: 'שם מבחן', render: (r) => (r._total ? '' : r.name || '—') },
                          { key: 'type', label: 'סוג', render: (r) => (r._total ? '' : EXAM_TYPE_LABELS[r.type] || r.type || '—') },
                          { key: 'count', label: 'רשומים', render: (r) => <Tag tone="primary"><b>{r.count}</b></Tag> },
                        ]}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <PanelGrid cols={2}>
          <Panel title="רישומים לפי מבחן" sub={`${byExam.length}`} icon="task" flush>
            <SimpleTable
              rowKey="code"
              rows={byExam}
              loading={first}
              empty="אין רישומים למבחנים"
              maxHeight={420}
              columns={[
                { key: 'code', label: 'קוד', render: (r) => codeTag(r.code) },
                { key: 'name', label: 'שם מבחן' },
                { key: 'type', label: 'סוג', render: (r) => EXAM_TYPE_LABELS[r.type] || r.type || '—' },
                { key: 'count', label: 'רישומים', render: (r) => <Tag tone="primary"><b>{r.count}</b></Tag> },
                { key: 'uniqueMembers', label: 'נבחנים ייחודיים', render: (r) => <Tag tone="info"><b>{r.uniqueMembers}</b></Tag> },
              ]}
            />
          </Panel>
          <Panel title="נבחנים לפי כולל" sub={`${byKolel.length}`} icon="building" flush>
            <SimpleTable
              rowKey={(r) => `${r.kolelCode}-${r.kolelName}`}
              rows={byKolel}
              loading={first}
              empty="אין נתונים"
              maxHeight={420}
              columns={[
                { key: 'city', label: 'עיר' },
                { key: 'kolelName', label: 'שם כולל' },
                { key: 'kolelCode', label: 'קוד', render: (r) => codeTag(r.kolelCode) },
                { key: 'uniqueMembers', label: 'נבחנים ייחודיים', render: (r) => <Tag tone="success"><b>{r.uniqueMembers}</b></Tag> },
              ]}
            />
          </Panel>
        </PanelGrid>
      </div>
    </Panel>
  );
}
